// src/pages/admin/AdminGalleryFolder.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Image as ImageIcon,
  Video,
  Trash2,
  Plus,
  PlayCircle,
  X,
  Loader2,
} from "lucide-react";

import AdminLayout from "./AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { FALLBACK_IMAGE, normalizeImageUrl } from "../../lib/utils";

import {
  getAdminGalleryFolderBySlug,
  addAdminGalleryImages,
  addAdminGalleryYoutube,
  deleteAdminGalleryItem,
} from "../../lib/adminApi";

function youtubeThumb(ytId) {
  if (!ytId) return null;
  return `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
}

export default function AdminGalleryFolder() {
  const { slug } = useParams();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");

  const [ytUrl, setYtUrl] = useState("");
  const [ytCaption, setYtCaption] = useState("");

  const [preview, setPreview] = useState(null);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [addingVideo, setAddingVideo] = useState(false);
  const [deletingIds, setDeletingIds] = useState([]);

  const images = data?.images || [];
  const videos = data?.videos || [];
  const folderId = data?.folder?.id;
  const folderName = data?.folder?.name || "Gallery Folder";

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAdminGalleryFolderBySlug(slug);
      setData(res);
    } catch (e) {
      toast({
        title: "Failed to load folder",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshWithoutPageLoading = async () => {
    try {
      const res = await getAdminGalleryFolderBySlug(slug);
      setData(res);
    } catch (e) {
      toast({
        title: "Failed to refresh folder",
        description: e.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!preview) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setPreview(null);
    };

    document.addEventListener("keydown", onKeyDown);

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = original;
    };
  }, [preview]);

  const uploadImages = async () => {
    if (!folderId) return;

    if (!files?.length) {
      toast({
        title: "Select at least one image",
        variant: "destructive",
      });
      return;
    }

    setUploadingImages(true);

    try {
      await addAdminGalleryImages(folderId, files, caption);

      toast({ title: "Images uploaded" });

      setFiles([]);
      setCaption("");

      await refreshWithoutPageLoading();
    } catch (e) {
      toast({
        title: "Upload failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setUploadingImages(false);
    }
  };

  const addYoutube = async () => {
    if (!folderId) return;

    if (!ytUrl.trim()) {
      toast({
        title: "YouTube link is required",
        variant: "destructive",
      });
      return;
    }

    setAddingVideo(true);

    try {
      await addAdminGalleryYoutube(folderId, ytUrl, ytCaption);

      toast({ title: "YouTube video added" });

      setYtUrl("");
      setYtCaption("");

      await refreshWithoutPageLoading();
    } catch (e) {
      toast({
        title: "Add failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setAddingVideo(false);
    }
  };

  const removeItem = async (item, type) => {
    const yes = window.confirm("Delete this item?");
    if (!yes) return;

    setDeletingIds((prev) => [...prev, item.id]);

    const oldData = data;

    setData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        images:
          type === "image"
            ? (prev.images || []).filter((img) => img.id !== item.id)
            : prev.images || [],
        videos:
          type === "video"
            ? (prev.videos || []).filter((v) => v.id !== item.id)
            : prev.videos || [],
      };
    });

    try {
      await deleteAdminGalleryItem(item.id);

      toast({ title: "Item deleted" });

      if (preview && preview.id === item.id) {
        setPreview(null);
      }
    } catch (e) {
      setData(oldData);

      toast({
        title: "Delete failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== item.id));
    }
  };

  return (
    <AdminLayout
      title={`Gallery • ${folderName}`}
      onRefresh={load}
      right={
        <Button asChild variant="outline" className="rounded-xl">
          <Link to="/admin/gallery">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to folders
          </Link>
        </Button>
      }
    >
      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="h-56 rounded-2xl bg-stone-100 animate-pulse" />
          <div className="h-56 rounded-2xl bg-stone-100 animate-pulse" />
        </div>
      ) : !data?.folder ? (
        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
          Folder not found.
        </div>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-5">
                <p className="font-semibold text-stone-900">Upload Photos</p>
                <p className="text-sm text-stone-600 mt-1">
                  Upload one or multiple images. Optional caption applies to all
                  uploaded images.
                </p>

                <div className="mt-4 space-y-3">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    disabled={uploadingImages}
                    onChange={(e) =>
                      setFiles(Array.from(e.target.files || []))
                    }
                    className="block w-full text-sm"
                  />

                  <input
                    value={caption}
                    disabled={uploadingImages}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30 disabled:bg-stone-100 disabled:cursor-not-allowed"
                  />

                  <Button
                    className="rounded-xl bg-amber-700 hover:bg-amber-800"
                    onClick={uploadImages}
                    disabled={uploadingImages}
                  >
                    {uploadingImages ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-stone-200 shadow-sm">
              <CardContent className="p-5">
                <p className="font-semibold text-stone-900">
                  Add YouTube Video
                </p>
                <p className="text-sm text-stone-600 mt-1">
                  Paste a YouTube link. We store it and display it on the public
                  gallery page.
                </p>

                <div className="mt-4 space-y-3">
                  <input
                    value={ytUrl}
                    disabled={addingVideo}
                    onChange={(e) => setYtUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30 disabled:bg-stone-100 disabled:cursor-not-allowed"
                  />

                  <input
                    value={ytCaption}
                    disabled={addingVideo}
                    onChange={(e) => setYtCaption(e.target.value)}
                    placeholder="Caption (optional)"
                    className="w-full rounded-xl border border-stone-200 px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/30 disabled:bg-stone-100 disabled:cursor-not-allowed"
                  />

                  <Button
                    className="rounded-xl bg-stone-900 hover:bg-black"
                    onClick={addYoutube}
                    disabled={addingVideo}
                  >
                    {addingVideo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Video
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-2xl border-stone-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-stone-900">Photos</p>
                <p className="text-xs text-stone-500">
                  {images.length} items
                </p>
              </div>

              {images.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
                  No photos yet.
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((img) => {
                    const deleting = deletingIds.includes(img.id);

                    return (
                      <div
                        key={img.id}
                        className={`rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 transition ${
                          deleting ? "opacity-50 pointer-events-none" : ""
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setPreview({
                              id: img.id,
                              type: "image",
                              url: img.url,
                              caption: img.caption,
                            })
                          }
                          className="block w-full"
                        >
                          <div className="aspect-square relative group">
                            <img
                              src={normalizeImageUrl(img.url)}
                              alt={img.caption || "Photo"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => {
                                e.currentTarget.src = FALLBACK_IMAGE;
                              }}
                            />

                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                              <p className="text-white text-xs line-clamp-2 flex items-center gap-2">
                                <ImageIcon className="w-3.5 h-3.5" />
                                {img.caption || "Photo"}
                              </p>
                            </div>
                          </div>
                        </button>

                        <div className="p-3 flex items-center justify-between gap-2">
                          <p className="text-xs text-stone-600 line-clamp-1">
                            {img.caption || "—"}
                          </p>

                          <Button
                            variant="destructive"
                            className="rounded-xl"
                            disabled={deleting}
                            onClick={() => removeItem(img, "image")}
                          >
                            {deleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-stone-200 shadow-sm mt-6">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-stone-900">YouTube Videos</p>
                <p className="text-xs text-stone-500">
                  {videos.length} items
                </p>
              </div>

              {videos.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-6 text-stone-600">
                  No videos yet.
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {videos.map((v) => {
                    const deleting = deletingIds.includes(v.id);

                    return (
                      <div
                        key={v.id}
                        className={`rounded-2xl overflow-hidden border border-stone-200 bg-stone-100 transition ${
                          deleting ? "opacity-50 pointer-events-none" : ""
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setPreview({
                              id: v.id,
                              type: "youtube",
                              url: v.url,
                              caption: v.caption,
                              youtube_id: v.youtube_id,
                            })
                          }
                          className="block w-full text-left"
                        >
                          <div className="aspect-video relative bg-black group">
                            <img
                              src={youtubeThumb(v.youtube_id) || FALLBACK_IMAGE}
                              alt={v.caption || "YouTube Video"}
                              className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
                              onError={(e) => {
                                e.currentTarget.src = FALLBACK_IMAGE;
                              }}
                            />

                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-white/15 border border-white/25 backdrop-blur flex items-center justify-center">
                                <PlayCircle className="w-7 h-7 text-white" />
                              </div>
                            </div>

                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                              <p className="text-white text-xs line-clamp-2 flex items-center gap-2">
                                <Video className="w-3.5 h-3.5" />
                                {v.caption || "YouTube Video"}
                              </p>
                            </div>
                          </div>
                        </button>

                        <div className="p-3 flex items-center justify-between gap-2">
                          <p className="text-xs text-stone-600 line-clamp-1">
                            {v.caption || "—"}
                          </p>

                          <Button
                            variant="destructive"
                            className="rounded-xl"
                            disabled={deleting}
                            onClick={() => removeItem(v, "video")}
                          >
                            {deleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {preview && (
            <div
              className="fixed inset-0 z-50"
              role="dialog"
              aria-modal="true"
              aria-label="Media preview"
            >
              <button
                type="button"
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => setPreview(null)}
                aria-label="Close preview"
              />

              <div className="relative z-10 h-full w-full p-4 sm:p-6 flex items-start justify-center">
                <div className="w-full max-w-4xl rounded-3xl overflow-hidden bg-white shadow-2xl border border-stone-200 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col">
                  <div className="flex items-center justify-between gap-4 p-4 sm:p-5 border-b border-stone-200">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-amber-700">
                        Preview
                      </p>
                      <p className="font-heading text-base sm:text-lg font-bold text-stone-900 mt-1 truncate">
                        {preview.caption || "Media"}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setPreview(null)}
                      className="w-10 h-10 rounded-full border border-stone-200 bg-white hover:bg-stone-50 flex items-center justify-center shrink-0"
                      aria-label="Close preview"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="p-4 sm:p-5 overflow-auto">
                    <div className="rounded-2xl overflow-hidden border border-stone-200 bg-stone-100">
                      {preview.type === "youtube" ? (
                        <div className="aspect-video w-full bg-black">
                          <iframe
                            className="w-full h-full"
                            src={
                              preview.youtube_id
                                ? `https://www.youtube.com/embed/${preview.youtube_id}`
                                : undefined
                            }
                            title={preview.caption || "YouTube video"}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <img
                          src={normalizeImageUrl(preview.url)}
                          alt={preview.caption || "Preview"}
                          className="w-full h-auto object-contain"
                          onError={(e) => {
                            e.currentTarget.src = FALLBACK_IMAGE;
                          }}
                        />
                      )}
                    </div>

                    {preview.caption ? (
                      <p className="text-stone-600 mt-4 leading-relaxed">
                        {preview.caption}
                      </p>
                    ) : null}

                    <div className="mt-5 flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full border-2"
                        onClick={() => setPreview(null)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}